/**
 * Область набора.
 *
 * Пробел отрисовывается обычным символом U+0020 и подсвечивается стилем:
 * подмена на неразрывный пробел сделала бы отображаемый текст не равным
 * целевому.
 *
 * Каждый символ — отдельный элемент с собственным состоянием, поэтому при
 * нажатии перерисовывается один символ, а не вся страница (NFR-P-03).
 *
 * Окно текста постоянной высоты — три-четыре строки (ТЗ UI-45). Оно не
 * прокручивается на строку вслед за набором, а переключается целиком, когда
 * текущий символ уходит за нижний край: строка, ползущая под пальцами,
 * заставляет глаз догонять её вместо того, чтобы читать вперёд. Прежнее
 * решение — прокрутка внутри области (AD-213) — этим и отменено.
 *
 * Высота окна постоянна ещё и потому, что от неё зависит, поместится ли
 * занятие в экран: длинное письмо не должно уводить метрики и клавиатуру за
 * нижний край (ТЗ UI-44, FR-EN-84).
 */

import { useEffect, useRef } from 'react';

export type CharacterState = 'pending' | 'correct' | 'incorrect' | 'current';

export interface TypingViewProps {
  readonly targetText: string;
  readonly typedText: string;
  readonly position: number;
  /** Подпись области для экранных читалок. */
  readonly label: string;
}

function stateOf(
  index: number,
  position: number,
  expected: string,
  actual: string | undefined,
): CharacterState {
  if (index === position) return 'current';
  if (index > position) return 'pending';
  return actual === expected ? 'correct' : 'incorrect';
}

export function TypingView({
  targetText,
  typedText,
  position,
  label,
}: TypingViewProps): React.JSX.Element {
  const expected = [...targetText];
  const actual = [...typedText];
  const current = useRef<HTMLSpanElement>(null);
  const viewport = useRef<HTMLParagraphElement>(null);

  /*
   * Переключение окна.
   *
   * Пока текущий символ виден, не делается ничего — ни записи, ни движения.
   * Как только он вышел за край, окно переставляется так, чтобы его строка
   * стала первой: это шаг страницы, а не прокрутка. `scrollTop` меняется
   * присваиванием, поэтому переключение мгновенно даже там, где кто-нибудь
   * включит плавную прокрутку глобально.
   *
   * Геометрия читается у самих элементов: в jsdom она нулевая, и тогда
   * условие не выполняется — окно просто не двигается.
   */
  useEffect(() => {
    const cursor = current.current;
    const box = viewport.current;
    if (cursor === null || box === null) return;

    const offset = cursor.getBoundingClientRect().top - box.getBoundingClientRect().top;
    const below = offset + cursor.offsetHeight > box.clientHeight;

    if (offset < 0 || below) box.scrollTop += offset;
  }, [position]);

  return (
    <p className="t2x-typing" ref={viewport} aria-label={label} role="textbox" aria-readonly="true">
      {expected.map((character, index) => (
        <span
          // Позиция в тексте стабильна и уникальна: символы могут повторяться.
          key={`${index}-${character}`}
          ref={index === position ? current : undefined}
          className={[
            't2x-typing__char',
            `t2x-typing__char--${stateOf(index, position, character, actual[index])}`,
            character === ' ' ? 't2x-typing__char--space' : '',
          ]
            .filter((item) => item !== '')
            .join(' ')}
        >
          {character}
        </span>
      ))}
    </p>
  );
}
